import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function DispatchItemsEditor({ dispatch, onClose, onSaved }) {
  const [items, setItems] = useState(() => (dispatch.items || []).map((it) => ({ product_id: it.product_id, name: it.name, unit: it.unit, quantity: it.quantity })));
  const [products, setProducts] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [addId, setAddId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || []));
    api.get(`/branch-stock?branch_id=${dispatch.from_branch_id}`).then((r) => {
      const m = {};
      (r.data || []).forEach((s) => { m[s.product_id] = Number(s.quantity || 0); });
      setStockMap(m);
    }).catch(() => {});
  }, [dispatch.from_branch_id]);

  const original = Object.fromEntries((dispatch.items || []).map((it) => [it.product_id, Number(it.quantity)]));
  const available = (pid) => (stockMap[pid] ?? 0) + (original[pid] || 0);
  const setQty = (i, v) => setItems((prev) => prev.map((it, x) => (x === i ? { ...it, quantity: v } : it)));
  const removeRow = (i) => setItems((prev) => prev.filter((_, x) => x !== i));
  const addRow = () => {
    const p = products.find((x) => x.id === addId);
    if (!p || items.some((it) => it.product_id === p.id)) return;
    setItems((prev) => [...prev, { product_id: p.id, name: p.name, unit: p.unit, quantity: "" }]);
    setAddId("");
  };

  const submit = async () => {
    const clean = items.map((it) => ({ product_id: it.product_id, name: it.name, unit: it.unit, quantity: Number(it.quantity) })).filter((it) => it.quantity > 0);
    if (!clean.length) return toast.error("Keep at least one line with a quantity");
    setSaving(true);
    try {
      const { data } = await api.put(`/dispatch/${dispatch.id}/items`, { items: clean, reason: reason || null });
      toast.success(`${data.number} items updated — stock & ledger recalculated (₹${Number(data.total_value || 0).toFixed(2)})`);
      onSaved?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update items"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="dispatch-items-editor">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Edit items — {dispatch.number}</DialogTitle>
          <DialogDescription>Super Admin only. Warehouse stock and FEFO batches are restored, then re-deducted with the new lines; the stock-transfer ledger entry is reversed and re-posted at the current HO Transfer Rate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1 max-h-[50vh] overflow-y-auto">
          {items.map((it, i) => (
            <div key={it.product_id} className="flex items-center gap-2 border border-[#EAE5D9] rounded-md p-2" data-testid={`dispatch-edit-row-${it.product_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{it.name}</div>
                <div className="text-[10px] text-[#8B7355]">Available at source incl. this dispatch: {available(it.product_id)} {it.unit}{original[it.product_id] != null ? ` · was ${original[it.product_id]}` : " · new line"}</div>
              </div>
              <Input type="number" min="0" step="0.001" value={it.quantity} onChange={(e) => setQty(i, e.target.value)} className="w-24 h-8 text-right" data-testid={`dispatch-edit-qty-${it.product_id}`} />
              <button onClick={() => removeRow(i)} className="text-red-500 hover:text-red-600 p-1" title="Remove line" data-testid={`dispatch-edit-remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex gap-2 items-center pt-1">
            <select className="flex-1 h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value={addId} onChange={(e) => setAddId(e.target.value)} data-testid="dispatch-edit-add-select">
              <option value="">Add a product…</option>
              {products.filter((p) => !items.some((it) => it.product_id === p.id)).map((p) => <option key={p.id} value={p.id}>{p.name} ({stockMap[p.id] ?? 0} {p.unit} at source)</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={addRow} disabled={!addId} data-testid="dispatch-edit-add-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. driver loaded 2 extra cartons" data-testid="dispatch-edit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="dispatch-edit-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & recalculate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
